import React, { useState, useEffect } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
  Fab,
  Radio,
  RadioGroup,
  FormLabel,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import * as yup from "yup";
import { useFormik } from "formik";
import FormControl from '@mui/material/FormControl';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ClearIcon from '@mui/icons-material/Clear';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles';
import { MenuItem } from "@mui/material";
import { get_payment_terms_list } from "../../../store/company/companySlice";
import { useDispatch, useSelector } from "react-redux";
import Stack from "@mui/material/Stack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from '@mui/icons-material/Delete';



const EMAIL_REGX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

function CreateCompany({
  is_company_modal_open,
  handleCompanyModal,
  handleAssignProcessCompany,
  is_loading,
  handleCompanySubmit,
  handleCompanyUpdate,
  c_action,
  company_id,
  selected_data,
  process_list,
  company_process
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [previewImage, setPreviewImage] = useState(null);
  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });



  const handleClose = () => {
    formik.resetForm();
    setPreviewImage(null);
    handleCompanyModal(false);
  };

  const dispatch = useDispatch();
  const paymentOptions = useSelector((state) => state.company.paymentOptions || []);


  useEffect(() => {
    if (paymentOptions.length === 0) {
      dispatch(get_payment_terms_list());
    }
  }, [dispatch, paymentOptions.length]);


  const validationSchema = yup.object().shape({
    name: yup.string().trim().required("Company Name is required."),
    company_email: yup
      .string()
      .matches(EMAIL_REGX, "Invalid email address.")
      .required("Email is required."),
    company_domain: yup.string().trim().required("Company Domain is required."),
    payment_type: yup.string().required("Payment Type is required."),
    rows: yup.array().of(
      yup.object().shape({
        process_id: yup
          .number()
          .required("Please choose a process."),
        display_order: yup.number(),
        has_proof: yup.boolean(), // Required: Yes=true, No=false
        proof_feature: yup.boolean().nullable().when('has_proof', {
          is: true,
          then: () => yup.boolean().nullable().required("Select a proof type."),
          otherwise: () => yup.boolean().nullable()
        }),
        reminder: yup.boolean(),
      })
    ),
  });

  const onSubmit = async (values) => {
    const process_data = values.rows
      .filter(row => row.process_id)
      .map(row => ({
        assigned_id: Number(row.process_id),
        display_order: row.display_order ? Number(row.display_order) : 1,
        // Map UI logic to Backend expected logic
        standard_proof: row.has_proof && row.proof_feature === false,
        annotated_proof: row.has_proof && row.proof_feature === true,
        // Assuming backend might accept reminder in future, or we just ignore it for now if not supported
        reminder: row.reminder
      }));
    const processIds = process_data.map(p => p.assigned_id);
    const duplicates = processIds.filter((id, index) => processIds.indexOf(id) !== index);
    if (duplicates.length > 0) {
      alert(`Duplicate process selected: ${[...new Set(duplicates)].join(", ")}`);
      return;
    }

    let response;
    if (c_action === "UPDATE") {
      response = await handleCompanyUpdate({
        company_id,
        updated_data: {
          name: values.name,
          company_email: values.company_email,
          company_domain: values.company_domain,
          payment_type: values.payment_type,
          company_logo: values.company_logo,
          process_data,
        },
      });

      if (response && !response.error) {
        const existingProcessIds = company_process?.map(p => p.id) || [];
        const unassigned_ids = existingProcessIds
          .filter(id => !process_data.some(p => p.assigned_id === id))
          .map(id => ({ assigned_id: id }));

        if (process_data.length > 0 || unassigned_ids.length > 0) {
          await handleAssignProcessCompany({
            company_id,
            assigned_ids: process_data,
            unassigned_ids,
          });
        }

        formik.resetForm();
        handleCompanyModal(false);
      }

    } else {
      response = await handleCompanySubmit({ ...values, process_data });
      formik.resetForm();
      handleCompanyModal(false);

      if (response && !response.error) {
        if (process_data.length > 0) {
          await handleAssignProcessCompany({
            company_id: response.data.id,
            assigned_ids: process_data,
            unassigned_ids: [],
          });
        }

        formik.resetForm();
        setPreviewImage(null);
        handleCompanyModal(false);
        window.location.reload()
      }
    }
  };

  // Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: c_action === "UPDATE" ? selected_data?.name || "" : "",
      company_email: c_action === "UPDATE" ? selected_data?.company_email || "" : "",
      company_domain: c_action === "UPDATE" ? selected_data?.company_domain || "" : "",
      payment_type: c_action === "UPDATE" ? selected_data?.payment_terms?.id : '',
      company_logo: null,
      rows:
        c_action === "UPDATE" && company_process?.length > 0
          ? company_process.map((proc) => ({
            process_id: proc.id,
            display_order: proc.display_order || "",
            // Logic to determine initial UI state from backend data
            has_proof: proc.annotated_proof === true || proc.standard_proof === true,
            proof_feature: proc.annotated_proof === true ? true : (proc.standard_proof === true ? false : null),
            reminder: false, // Defaulting to false as it's not in the read model yet
            chose: true,
          }))
          : [{ process_id: "", display_order: "", has_proof: false, proof_feature: null, reminder: false, chose: false }],

    },
    validationSchema,
    onSubmit,
  });

  useEffect(() => {
    if (formik.values.company_logo) {
      const fileReader = new FileReader();
      fileReader.onload = () => setPreviewImage(fileReader.result);
      fileReader.readAsDataURL(formik.values.company_logo);
    } else {
      setPreviewImage(null);
    }
  }, [formik.values.company_logo]);

  return (
    <Dialog
      fullScreen={fullScreen}
      open={is_company_modal_open}
      onClose={handleClose}
      maxWidth="md"
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: { xs: '95%', sm: '80%', md: '50%' },
        },
      }}
    >
      <DialogTitle id="responsive-dialog-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {(c_action === 'UPDATE' && 'Update Company') || (c_action === 'CREATE' && 'Create Company')}
        <IconButton size="medium" onClick={handleClose}>
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent
          sx={{
            '& .MuiTextField-root': {
              my: 1,
            },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 4 }, mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                label="Company Name *"
                name="name"
                size="small"
                fullWidth
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextField
                label="Company Domain *"
                name="company_domain"
                size="small"
                fullWidth
                value={formik.values.company_domain}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.company_domain && Boolean(formik.errors.company_domain)}
                helperText={formik.touched.company_domain && formik.errors.company_domain}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1
                  }
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 4 }, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                label="Company Email *"
                name="company_email"
                size="small"
                fullWidth
                value={formik.values.company_email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.company_email && Boolean(formik.errors.company_email)}
                helperText={formik.touched.company_email && formik.errors.company_email}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextField
                select
                label="Payment Type *"
                name="payment_type"
                size="small"
                fullWidth
                value={formik.values.payment_type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.payment_type && Boolean(formik.errors.payment_type)}
                helperText={formik.touched.payment_type && formik.errors.payment_type}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1
                  }
                }}
              >
                {paymentOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.payment_type}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>


          <FormControl error={formik.touched.company_logo && Boolean(formik.errors.company_logo)}
            sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 3 }}
          >
            <Button
              component="label"
              disableElevation
              variant="contained"
              startIcon={<CloudUploadIcon />}
              sx={{
                fontSize: '16px',
                backgroundColor: '#1976d2', // Blue to match screenshot
                textTransform: 'uppercase',
                px: 3,
                py: 1.5,
                width: 'auto',
                minWidth: '200px',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: '#1565c0'
                }
              }}
            >
              {is_loading && formik.values.company_logo ? 'Uploading...' : 'COMPANY LOGO'}
              <VisuallyHiddenInput
                type="file"
                accept="image/*"
                onChange={(event) => {
                  const file = event.target.files[0];
                  formik.setFieldValue('company_logo', file);
                }}
              />
            </Button>

            {/* Preview Section */}
            {previewImage && (
              <Box
                sx={{
                  marginTop: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <img
                  src={previewImage}
                  alt="Preview"
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'contain',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => formik.setFieldValue('company_logo', null)}
                >
                  <ClearIcon />
                </IconButton>
              </Box>
            )}

            {!previewImage && c_action === "UPDATE" && selected_data.company_logo && (
              <Box sx={{ marginTop: 2 }}>
                <img
                  src={selected_data.company_logo}
                  alt="Current Logo"
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'contain',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </Box>
            )}

          </FormControl>

          {formik.values.rows?.map((row, index) => (
            <Stack
              key={index}
              direction={{ xs: 'column', lg: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', lg: 'center' }}
              sx={{ mb: 3 }}
            >
              <Typography sx={{ pt: { xs: 0, lg: 1 }, minWidth: '20px' }}>
                {index + 1}.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  select
                  label="Choose Process *"
                  name={`rows[${index}].process_id`}
                  size="small"
                  value={formik.values.rows[index]?.process_id || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputLabelProps={{ shrink: true }}
                  error={
                    formik.touched.rows?.[index]?.process_id &&
                    Boolean(formik.errors.rows?.[index]?.process_id)
                  }
                  sx={{ width: { xs: '100%', md: 200 }, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                >
                  {process_list.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Display *"
                  variant="outlined"
                  size="small"
                  type="number"
                  name={`rows[${index}].display_order`}
                  value={formik.values.rows[index]?.display_order || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: { xs: '100%', md: 80 }, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                />
              </Box>


              {/* Enhanced Proof Feature Section */}
              <Box sx={{
                border: '1px solid #c4c4c4',
                borderRadius: '4px',
                p: 1.5,
                position: 'relative',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                // alignItems: { md: 'center' },
                gap: 2
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    top: -10,
                    left: 10,
                    bgcolor: 'white',
                    px: 0.5,
                    color: '#666'
                  }}
                >
                  Proof Feature*
                </Typography>

                {/* Required Section: Yes/No */}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="caption" sx={{ color: '#666', mb: 0.5, fontWeight: 500 }}>Required</Typography>
                  <RadioGroup
                    name={`rows[${index}].has_proof`}
                    value={formik.values.rows[index]?.has_proof === true ? "true" : "false"}
                    onChange={(e) => {
                      const val = e.target.value === 'true';
                      formik.setFieldValue("rows[" + index + "].has_proof", val);
                      if (!val) {
                        formik.setFieldValue("rows[" + index + "].proof_feature", null);
                      }
                    }}
                  >
                    <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" sx={{ height: 26 }} />
                    <FormControlLabel value="false" control={<Radio size="small" />} label="No" sx={{ height: 26 }} />
                  </RadioGroup>
                </Box>

                {/* Type Section: Annotation/Standard */}
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  pl: 2,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '46%',
                    bottom: '15%',
                    width: '2px',
                    bgcolor: '#a7a3a3ff',
                    display: { xs: 'none', md: 'block' }
                  },
                  opacity: formik.values.rows[index]?.has_proof ? 1 : 0.5,
                  pointerEvents: formik.values.rows[index]?.has_proof ? 'auto' : 'none'
                }}>
                  <Typography variant="caption" sx={{ color: '#666', mb: 0.5, fontWeight: 500 }}>Type</Typography>
                  <RadioGroup
                    name={`rows[${index}].proof_feature`}
                    value={
                      formik.values.rows[index]?.proof_feature === true ? "true" :
                        formik.values.rows[index]?.proof_feature === false ? "false" : ""
                    }
                    onChange={(e) => {
                      formik.setFieldValue(`rows[${index}].proof_feature`, e.target.value === "true");
                    }}
                  >
                    <FormControlLabel value="true" control={<Radio size="small" />} label="Annotation" sx={{ height: 26 }} />
                    <FormControlLabel value="false" control={<Radio size="small" />} label="Standard" sx={{ height: 26 }} />
                  </RadioGroup>
                </Box>

                {/* Reminder Checkbox - Centered Relative to Radio Groups */}
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  pl: 2,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '46%',
                    bottom: '15%',
                    width: '2px',
                    bgcolor: '#a7a3a3ff',
                    display: { xs: 'none', md: 'block' }
                  },
                  opacity: formik.values.rows[index]?.has_proof ? 1 : 0.5,
                  pointerEvents: formik.values.rows[index]?.has_proof ? 'auto' : 'none'
                }}>
                  {/* Spacer to match Header height */}
                  <Typography variant="caption" sx={{ color: 'transparent', mb: 0.5, fontWeight: 500 }}>Spacer</Typography>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="medium"
                          checked={formik.values.rows[index]?.reminder || false}
                          onChange={formik.handleChange}
                          name={`rows[${index}].reminder`}
                        />
                      }
                      label="Reminder"
                    />
                  </Box>
                </Box>

              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <IconButton
                  color="primary"
                  onClick={() => {
                    const lastRow = formik.values.rows[formik.values.rows.length - 1];
                    if (!lastRow?.process_id) {
                      formik.setFieldError(
                        `rows[${formik.values.rows.length - 1}].process_id`,
                        "Please choose a process first."
                      );
                      return;
                    }
                    formik.setFieldValue("rows", [
                      ...formik.values.rows,
                      { process_id: "", display_order: "", has_proof: false, proof_feature: null, reminder: false, chose: false },
                    ]);
                  }}
                >
                  <AddIcon fontSize="large" />
                </IconButton>

                <IconButton
                  color="primary"
                  onClick={() => {
                    if (formik.values.rows.length >= 1) { // Allow delete always, or logic as per requirement
                      const confirmDelete = window.confirm("Are you sure you want to delete this row?");
                      if (confirmDelete) {
                        const updatedRows = formik.values.rows.filter((_, i) => i !== index);
                        formik.setFieldValue("rows", updatedRows);
                      }
                    }
                  }}
                >
                  <DeleteIcon fontSize="large" />
                </IconButton>

              </Box>

            </Stack>
          ))}

        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              borderColor: "#ff6100",
              color: "#666",
              textTransform: 'uppercase'
            }}
          >
            CANCEL
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={is_loading}
            sx={{
              backgroundColor: "#ff6100",
              textTransform: 'uppercase',
              '&:hover': {
                backgroundColor: '#e65100'
              }
            }}
          >
            {c_action === "UPDATE" ? "UPDATE" : "SUBMIT"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default CreateCompany;
